import { useEffect, useRef } from "react";

import type { AddByHandDraft, Id, Screen } from "../api/types";

/*
  "Point to" (2.7.4, 2.10): the agent shows the person where something is. The message does not move
  the person; it goes to this bus. A screen subscribes to highlight what the agent pointed at, and
  the agent panel offers a button that opens the screen.
*/

export interface PointToEvent {
  /** Counts up, so a screen can tell a new event from one it has seen. */
  id: number;
  screen: Screen;
  element_id?: Id | null;
  spot_id?: Id | null;
  sentence: string;
  form_draft?: AddByHandDraft | null;
  /** When it was sent (milliseconds). */
  at: number;
}

export type PointToInput = Omit<PointToEvent, "id" | "at">;
type Listener = (event: PointToEvent) => void;

/** How long a screen that opens a moment later may still pick up the event. */
export const POINT_TO_LIFETIME_MS = 15_000;

let counter = 0;
let latest: PointToEvent | null = null;
const listeners = new Set<Listener>();

export const pointToBus = {
  emit(input: PointToInput, now: number = Date.now()): PointToEvent {
    counter += 1;
    const event: PointToEvent = { ...input, id: counter, at: now };
    latest = event;
    for (const listener of [...listeners]) listener(event);
    return event;
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** The last event, if it is still fresh. */
  latest(now: number = Date.now()): PointToEvent | null {
    return latest && now - latest.at <= POINT_TO_LIFETIME_MS ? latest : null;
  },
  clear(): void {
    latest = null;
    listeners.clear();
  },
};

/**
 * Highlight what the agent pointed at. `handler` is called for each event on one of `screens`, and
 * once at the start for a fresh event the screen missed because it was still opening.
 */
export function usePointTo(handler: (event: PointToEvent) => void, options: { screens?: readonly Screen[] } = {}): void {
  const handlerRef = useRef(handler);
  const screensKey = (options.screens ?? []).join("|");
  useEffect(() => {
    handlerRef.current = handler;
  });
  useEffect(() => {
    const wanted = screensKey ? new Set(screensKey.split("|")) : null;
    const accepts = (event: PointToEvent) => !wanted || wanted.has(event.screen);
    const fresh = pointToBus.latest();
    if (fresh && accepts(fresh)) handlerRef.current(fresh);
    return pointToBus.subscribe((event) => {
      if (accepts(event)) handlerRef.current(event);
    });
  }, [screensKey]);
}
